// Owner analytics — pure aggregation + bucketing math.
//
// TENANT ISOLATION CONTRACT:
//   Every function in this module is pure: it derives its output solely from
//   the rows passed in as arguments. It performs NO database access, reads NO
//   global/module state, and holds NO caches. Whatever the caller fetched —
//   already RLS/tenant-scoped by the admin policies — is the entire universe
//   these helpers can see. They cannot widen scope beyond the provided rows,
//   which is what makes them safe to unit test and impossible to leak across
//   tenants. See analytics.test.ts for the isolation reasoning test.
//
// All money is in integer cents. All dates are ISO `YYYY-MM-DD` (SQL `date`)
// or full ISO timestamps; helpers that bucket by period parse the leading
// 10 characters so a timestamp and a date string bucket identically.

// ---------------------------------------------------------------------------
// Shared row shapes — intentionally minimal structural subsets of the DB rows.
// Helpers accept the narrowest shape they need so callers can pass richer rows.
// ---------------------------------------------------------------------------

export type EnrollmentStatus = 'enrolled' | 'waitlisted' | 'dropped';

export interface EnrollmentRow {
  status: string;
  start_date: string;
  end_date: string | null;
  deleted_at?: string | null;
}

export type ChargeStatus = 'pending' | 'paid' | 'partial' | 'void' | 'written_off';

export interface ChargeRow {
  amount_cents: number;
  status: string;
  due_date: string | null;
}

export interface PaymentRow {
  amount_cents: number;
  status: string;
  paid_at: string | null;
}

export interface AccountBalanceRow {
  outstanding_cents: number;
  open_count: number;
  overdue_count: number;
}

export type AttendanceStatus = 'present' | 'late' | 'absent' | 'excused';

export interface AttendanceRow {
  status: string;
  // Identifier of the grouping target (class or session). Helpers that group
  // repeat-absence by class take a `group_id`; at-risk-by-dancer take a
  // `dancer_id`. Both are optional so a row can serve either grouping.
  group_id?: string;
  group_label?: string;
  dancer_id?: string;
  dancer_label?: string;
}

export type TrialStatus = 'booked' | 'attended' | 'missed' | 'cancelled' | 'converted';

export interface LeadRow {
  lead_status: string;
  converted_family_id?: string | null;
}

export interface TrialRow {
  status: string;
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const DAY_KEY_LENGTH = 10; // 'YYYY-MM-DD'
const MONTH_KEY_LENGTH = 7; // 'YYYY-MM'

/** Leading day key (`YYYY-MM-DD`) from a date or timestamp string. */
export function dayKey(value: string): string {
  return value.slice(0, DAY_KEY_LENGTH);
}

/** Leading month key (`YYYY-MM`) from a date or timestamp string. */
export function monthKey(value: string): string {
  return value.slice(0, MONTH_KEY_LENGTH);
}

/**
 * ISO week key (`YYYY-Www`) for a `YYYY-MM-DD` date. Uses the ISO-8601 rule:
 * week 1 is the week containing the first Thursday; weeks start Monday.
 * Returns `null` for an unparseable input rather than throwing — analytics
 * should degrade to "skip this row" not crash the dashboard.
 */
export function isoWeekKey(value: string): string | null {
  const key = dayKey(value);
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return null;
  // Use UTC to avoid the local-timezone DST edge that can shift a date by a day.
  const date = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(date.getTime())) return null;
  // ISO: Thursday determines the year/week.
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // shift to the week's Thursday
  const isoYear = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const week = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 86400000));
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}

/**
 * True when `value`'s day is within [start, end] inclusive. `start`/`end` may
 * be `null` (open-ended). Comparison is lexicographic on `YYYY-MM-DD`, which is
 * correct for zero-padded ISO dates.
 */
export function isWithinRange(
  value: string | null,
  start: string | null,
  end: string | null,
): boolean {
  if (!value) return false;
  const k = dayKey(value);
  if (start && k < dayKey(start)) return false;
  if (end && k > dayKey(end)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Enrollment trend
// ---------------------------------------------------------------------------

export interface EnrollmentTrendPoint {
  period: string;
  started: number;
  dropped: number;
  net: number;
}

function isLiveEnrollment(row: EnrollmentRow): boolean {
  return !row.deleted_at;
}

/**
 * Bucket enrollment starts and drops by period. A row counts as a "start" in
 * the bucket of its `start_date`. A row counts as a "drop" in the bucket of its
 * `end_date` when status is `dropped` and an `end_date` exists. `net` is
 * started − dropped for that bucket (a per-period delta, not a running total).
 *
 * `granularity` selects week (ISO) or month buckets. Rows with an unbucketable
 * date are skipped rather than thrown.
 */
export function enrollmentTrend(
  rows: readonly EnrollmentRow[],
  granularity: 'week' | 'month' = 'month',
): EnrollmentTrendPoint[] {
  const keyFor = (value: string): string | null =>
    granularity === 'week' ? isoWeekKey(value) : monthKey(value);

  const buckets = new Map<string, { started: number; dropped: number }>();
  const bump = (period: string, field: 'started' | 'dropped') => {
    const b = buckets.get(period) ?? { started: 0, dropped: 0 };
    b[field] += 1;
    buckets.set(period, b);
  };

  for (const row of rows) {
    if (!isLiveEnrollment(row)) continue;
    const startBucket = keyFor(row.start_date);
    if (startBucket) bump(startBucket, 'started');
    if (row.status === 'dropped' && row.end_date) {
      const dropBucket = keyFor(row.end_date);
      if (dropBucket) bump(dropBucket, 'dropped');
    }
  }

  return [...buckets.entries()]
    .map(([period, b]) => ({
      period,
      started: b.started,
      dropped: b.dropped,
      net: b.started - b.dropped,
    }))
    .sort((a, b) => (a.period < b.period ? -1 : a.period > b.period ? 1 : 0));
}

export interface EnrollmentSnapshot {
  active: number;
  waitlisted: number;
  dropped: number;
  total: number;
}

/**
 * Headcount snapshot. "active" = enrolled AND not ended before `asOf` (or no
 * end date). `asOf` defaults to a far-future sentinel so, when omitted, any
 * non-dropped enrolled row counts as active.
 */
export function enrollmentSnapshot(
  rows: readonly EnrollmentRow[],
  asOf?: string,
): EnrollmentSnapshot {
  const cutoff = asOf ? dayKey(asOf) : null;
  let active = 0;
  let waitlisted = 0;
  let dropped = 0;
  for (const row of rows) {
    if (!isLiveEnrollment(row)) continue;
    if (row.status === 'dropped') {
      dropped += 1;
      continue;
    }
    if (row.status === 'waitlisted') {
      waitlisted += 1;
      continue;
    }
    if (row.status === 'enrolled') {
      const ended = cutoff !== null && row.end_date !== null && dayKey(row.end_date) < cutoff;
      if (!ended) active += 1;
    }
  }
  return { active, waitlisted, dropped, total: active + waitlisted + dropped };
}

// ---------------------------------------------------------------------------
// Retention / churn (best-effort from enrollment + drops)
// ---------------------------------------------------------------------------

export interface RetentionSummary {
  baseActive: number;
  dropped: number;
  retentionRate: number; // 0..1, share of base that did NOT drop in window
  churnRate: number; // 0..1, share of base that dropped in window
}

/**
 * Best-effort retention over a window. The denominator is the number of
 * enrollments that were active at the window start (`enrolled`, started on or
 * before `windowStart`, not ended before `windowStart`). The churn numerator
 * is how many of THOSE dropped (status `dropped`, `end_date` within the
 * window). Returns zeroes (rate 0) when the base is empty — the caller renders
 * "Not enough data yet" in that case.
 */
export function retentionSummary(
  rows: readonly EnrollmentRow[],
  windowStart: string,
  windowEnd: string,
): RetentionSummary {
  const startKey = dayKey(windowStart);
  let baseActive = 0;
  let dropped = 0;
  for (const row of rows) {
    if (!isLiveEnrollment(row)) continue;
    const startedBeforeWindow = dayKey(row.start_date) <= startKey;
    if (!startedBeforeWindow) continue;
    const endedBeforeWindow = row.end_date !== null && dayKey(row.end_date) < startKey;
    if (endedBeforeWindow) continue;
    // This enrollment was live at window start → part of the base.
    baseActive += 1;
    if (
      row.status === 'dropped' &&
      row.end_date !== null &&
      isWithinRange(row.end_date, windowStart, windowEnd)
    ) {
      dropped += 1;
    }
  }
  if (baseActive === 0) {
    return { baseActive: 0, dropped: 0, retentionRate: 0, churnRate: 0 };
  }
  const churnRate = dropped / baseActive;
  return {
    baseActive,
    dropped,
    retentionRate: 1 - churnRate,
    churnRate,
  };
}

// ---------------------------------------------------------------------------
// Revenue / AR
// ---------------------------------------------------------------------------

const ZERO_OUT_STATUSES = new Set<string>(['void', 'written_off']);

export interface RevenueSummary {
  grossChargedCents: number;
  collectedCents: number;
  outstandingCents: number;
  overdueCents: number;
  chargeCount: number;
  paymentCount: number;
}

/**
 * Gross charged = sum of charge amounts excluding void/written_off (those are
 * money that never changed hands). Collected = sum of succeeded payment
 * amounts. Outstanding = charges still pending/partial, counting only the
 * unpaid remainder is NOT possible from charge rows alone (allocations live
 * elsewhere), so outstanding here is the full amount of pending/partial charges
 * — use `arFromBalances` when you have the authoritative `account_balance`
 * rows. Overdue = pending/partial charges whose due_date is before `asOf`.
 */
export function revenueSummary(
  charges: readonly ChargeRow[],
  payments: readonly PaymentRow[],
  asOf: string,
): RevenueSummary {
  const today = dayKey(asOf);
  let grossChargedCents = 0;
  let outstandingCents = 0;
  let overdueCents = 0;
  let chargeCount = 0;
  for (const c of charges) {
    if (ZERO_OUT_STATUSES.has(c.status)) continue;
    grossChargedCents += c.amount_cents;
    chargeCount += 1;
    if (c.status === 'pending' || c.status === 'partial') {
      outstandingCents += c.amount_cents;
      if (c.due_date !== null && dayKey(c.due_date) < today) {
        overdueCents += c.amount_cents;
      }
    }
  }
  let collectedCents = 0;
  let paymentCount = 0;
  for (const p of payments) {
    if (p.status !== 'succeeded') continue;
    collectedCents += p.amount_cents;
    paymentCount += 1;
  }
  return {
    grossChargedCents,
    collectedCents,
    outstandingCents,
    overdueCents,
    chargeCount,
    paymentCount,
  };
}

export interface ArSummary {
  outstandingCents: number;
  openCount: number;
  overdueCount: number;
  familiesWithBalance: number;
}

/**
 * Authoritative AR rollup from `account_balance` / `family_balance` view rows
 * (which already net allocations against charges). `familiesWithBalance`
 * counts rows whose outstanding is strictly positive.
 */
export function arFromBalances(rows: readonly AccountBalanceRow[]): ArSummary {
  let outstandingCents = 0;
  let openCount = 0;
  let overdueCount = 0;
  let familiesWithBalance = 0;
  for (const r of rows) {
    outstandingCents += r.outstanding_cents;
    openCount += r.open_count;
    overdueCount += r.overdue_count;
    if (r.outstanding_cents > 0) familiesWithBalance += 1;
  }
  return { outstandingCents, openCount, overdueCount, familiesWithBalance };
}

export interface AgingBucket {
  label: string;
  minDays: number;
  maxDays: number | null; // null = no upper bound
  amountCents: number;
  count: number;
}

const DEFAULT_AGING_EDGES: readonly { label: string; min: number; max: number | null }[] = [
  { label: 'Current', min: -Infinity, max: 0 },
  { label: '1–30 days', min: 1, max: 30 },
  { label: '31–60 days', min: 31, max: 60 },
  { label: '61–90 days', min: 61, max: 90 },
  { label: '90+ days', min: 91, max: null },
];

const MS_PER_DAY = 86400000;

/** Whole days between two `YYYY-MM-DD` keys (b − a). Positive when b is later. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = dayKey(a).split('-').map(Number);
  const [by, bm, bd] = dayKey(b).split('-').map(Number);
  if (!ay || !am || !ad || !by || !bm || !bd) return 0;
  const ams = Date.UTC(ay, am - 1, ad);
  const bms = Date.UTC(by, bm - 1, bd);
  return Math.round((bms - ams) / MS_PER_DAY);
}

/**
 * AR aging buckets by days-past-due as of `asOf`. Only pending/partial charges
 * with a due_date participate; a charge with no due date or a non-open status
 * is excluded (it isn't a dated receivable). Bucketing uses days overdue =
 * asOf − due_date, so a not-yet-due charge (negative/zero) lands in "Current".
 */
export function agingBuckets(charges: readonly ChargeRow[], asOf: string): AgingBucket[] {
  const buckets: AgingBucket[] = DEFAULT_AGING_EDGES.map((e) => ({
    label: e.label,
    minDays: e.min === -Infinity ? Number.NEGATIVE_INFINITY : e.min,
    maxDays: e.max,
    amountCents: 0,
    count: 0,
  }));
  for (const c of charges) {
    if (c.status !== 'pending' && c.status !== 'partial') continue;
    if (c.due_date === null) continue;
    const overdueDays = daysBetween(c.due_date, asOf);
    const bucket = buckets.find((b) => {
      const aboveMin = b.minDays === Number.NEGATIVE_INFINITY || overdueDays >= b.minDays;
      const belowMax = b.maxDays === null || overdueDays <= b.maxDays;
      return aboveMin && belowMax;
    });
    if (bucket) {
      bucket.amountCents += c.amount_cents;
      bucket.count += 1;
    }
  }
  return buckets;
}

// ---------------------------------------------------------------------------
// Attendance
// ---------------------------------------------------------------------------

const PRESENT_STATUSES = new Set<string>(['present', 'late']);
const ABSENT_STATUSES = new Set<string>(['absent']);

export interface AttendanceRate {
  present: number; // present + late
  absent: number; // absent only (excused is neither present nor counted absent)
  excused: number;
  total: number; // present + late + absent + excused
  rate: number; // present / (present + absent); 0 when denominator is 0
}

/**
 * Studio-wide attendance rate. Present counts `present` and `late`. The rate
 * denominator is present + absent (excused absences are tracked but excluded
 * from the rate, matching how studios judge "did they show up when expected").
 */
export function attendanceRate(rows: readonly AttendanceRow[]): AttendanceRate {
  let present = 0;
  let absent = 0;
  let excused = 0;
  for (const r of rows) {
    if (PRESENT_STATUSES.has(r.status)) present += 1;
    else if (ABSENT_STATUSES.has(r.status)) absent += 1;
    else if (r.status === 'excused') excused += 1;
  }
  const denom = present + absent;
  return {
    present,
    absent,
    excused,
    total: present + absent + excused,
    rate: denom === 0 ? 0 : present / denom,
  };
}

export interface RepeatAbsenceGroup {
  id: string;
  label: string;
  absences: number;
  total: number;
  absenceRate: number;
}

/**
 * Classes (or any `group_id`) ranked by absence count. Only rows that carry a
 * `group_id` participate. `threshold` filters to groups with at least that many
 * absences (default 2 — "repeat" absence). Sorted by absences desc, then rate.
 */
export function repeatAbsenceGroups(
  rows: readonly AttendanceRow[],
  threshold = 2,
): RepeatAbsenceGroup[] {
  const acc = new Map<string, { label: string; absences: number; total: number }>();
  for (const r of rows) {
    if (!r.group_id) continue;
    const cur = acc.get(r.group_id) ?? {
      label: r.group_label ?? r.group_id,
      absences: 0,
      total: 0,
    };
    cur.total += 1;
    if (ABSENT_STATUSES.has(r.status)) cur.absences += 1;
    // Keep the first non-empty label we see.
    if (cur.label === r.group_id && r.group_label) cur.label = r.group_label;
    acc.set(r.group_id, cur);
  }
  return [...acc.entries()]
    .map(([id, v]) => ({
      id,
      label: v.label,
      absences: v.absences,
      total: v.total,
      absenceRate: v.total === 0 ? 0 : v.absences / v.total,
    }))
    .filter((g) => g.absences >= threshold)
    .sort((a, b) => b.absences - a.absences || b.absenceRate - a.absenceRate);
}

export interface AtRiskStudent {
  dancerId: string;
  label: string;
  absences: number;
  total: number;
  absenceRate: number;
}

/**
 * Students flagged at-risk by absence rate. A dancer qualifies when they have
 * at least `minSessions` recorded sessions AND their absence rate (absent /
 * total) meets `rateThreshold`. Sorted by rate desc, then absences desc.
 */
export function atRiskStudents(
  rows: readonly AttendanceRow[],
  rateThreshold = 0.3,
  minSessions = 3,
): AtRiskStudent[] {
  const acc = new Map<string, { label: string; absences: number; total: number }>();
  for (const r of rows) {
    if (!r.dancer_id) continue;
    const cur = acc.get(r.dancer_id) ?? {
      label: r.dancer_label ?? r.dancer_id,
      absences: 0,
      total: 0,
    };
    cur.total += 1;
    if (ABSENT_STATUSES.has(r.status)) cur.absences += 1;
    if (cur.label === r.dancer_id && r.dancer_label) cur.label = r.dancer_label;
    acc.set(r.dancer_id, cur);
  }
  return [...acc.entries()]
    .map(([dancerId, v]) => ({
      dancerId,
      label: v.label,
      absences: v.absences,
      total: v.total,
      absenceRate: v.total === 0 ? 0 : v.absences / v.total,
    }))
    .filter((s) => s.total >= minSessions && s.absenceRate >= rateThreshold)
    .sort((a, b) => b.absenceRate - a.absenceRate || b.absences - a.absences);
}

// ---------------------------------------------------------------------------
// Trial funnel
// ---------------------------------------------------------------------------

export interface TrialFunnel {
  leads: number;
  trialsScheduled: number;
  trialsAttended: number;
  converted: number;
  // Conversion of attended trials into enrolled families. 0 when no attended.
  attendedToConvertedRate: number;
  // Conversion of leads into enrolled families. 0 when no leads.
  leadToConvertedRate: number;
}

const SCHEDULED_TRIAL_STATUSES = new Set<string>([
  'booked',
  'attended',
  'converted',
  'missed',
  'cancelled',
]);
const ATTENDED_TRIAL_STATUSES = new Set<string>(['attended', 'converted']);

/**
 * Trial funnel from lead + trial-booking rows.
 *   leads            = count of all lead rows
 *   trialsScheduled  = trial bookings that were ever scheduled (any status that
 *                      implies a slot was held: booked/attended/converted/
 *                      missed/cancelled — i.e. every row, but kept explicit so
 *                      a future status doesn't silently count)
 *   trialsAttended   = trial bookings with status attended or converted
 *   converted        = lead rows that reached a converted_family_id OR
 *                      lead_status 'enrolled' (a lead counts once)
 * Conversion rates are derived ratios, guarded against divide-by-zero.
 */
export function trialFunnel(leads: readonly LeadRow[], trials: readonly TrialRow[]): TrialFunnel {
  const leadCount = leads.length;
  let converted = 0;
  for (const l of leads) {
    if (l.converted_family_id || l.lead_status === 'enrolled') converted += 1;
  }
  let trialsScheduled = 0;
  let trialsAttended = 0;
  for (const t of trials) {
    if (SCHEDULED_TRIAL_STATUSES.has(t.status)) trialsScheduled += 1;
    if (ATTENDED_TRIAL_STATUSES.has(t.status)) trialsAttended += 1;
  }
  return {
    leads: leadCount,
    trialsScheduled,
    trialsAttended,
    converted,
    attendedToConvertedRate: trialsAttended === 0 ? 0 : converted / trialsAttended,
    leadToConvertedRate: leadCount === 0 ? 0 : converted / leadCount,
  };
}

// ---------------------------------------------------------------------------
// Recital ops
// ---------------------------------------------------------------------------

export interface DanceCastRow {
  dance_id: string;
  dance_name: string;
  cast_count: number;
}

/**
 * Dances with no cast assigned. Pure: the caller pre-counts cast per dance (a
 * single grouped query) and passes the rollup; this just filters to zero-cast.
 */
export function dancesMissingCast(rows: readonly DanceCastRow[]): DanceCastRow[] {
  return rows
    .filter((r) => r.cast_count <= 0)
    .sort((a, b) => (a.dance_name < b.dance_name ? -1 : 1));
}

// ---------------------------------------------------------------------------
// Formatting helpers shared by the dashboard UI (kept here so they're tested
// alongside the math and reused by CSV export).
// ---------------------------------------------------------------------------

/** Render a 0..1 ratio as a whole-number percent string, e.g. 0.732 → "73%". */
export function formatRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
}

/** True when a dataset is effectively empty (nothing to honestly report). */
export function isEmptyDataset(...counts: number[]): boolean {
  return counts.every((c) => !c || c <= 0);
}
